import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { regionalOrderApi } from '../../services/api'
import Button from '../../components/common/Button'

/**
 * Receiving — chair records what arrived from each regional shipment.
 *
 * Workflow:
 *   1. Lands on a list of open regional orders (received_at on header is NULL)
 *   2. Picks one → expands to show all line items + qty inputs
 *   3. Enters received qty per line (defaults to ordered - already_received)
 *   4. Optionally checks "Mark this order fully received" to close it
 *   5. Submits → server FIFO-fills any open customer backorders
 *      and shows a summary toast (e.g. "Received 5 lines, 12 BO units filled")
 *
 * The chair can receive partially and come back later — received_quantity
 * accumulates, doesn't replace. Each subsequent submit adds more.
 */
export default function Receiving() {
    const [orders, setOrders] = useState([])
    const [loading, setLoading] = useState(true)
    const [expandedId, setExpandedId] = useState(null)

    useEffect(() => { load() }, [])

    async function load() {
        setLoading(true)
        try {
            const response = await regionalOrderApi.listOpenForReceiving()
            if (response.success) setOrders(response.orders)
            else toast.error(response.message || 'Could not load')
        } catch {
            toast.error('Network error')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="space-y-4">
            <div>
                <h2 className="text-2xl font-semibold text-ghasc-navy">Receiving</h2>
                <p className="text-sm text-gray-500 mt-1">
                    Record what arrived from regional shipments. Filled backorders
                    are allocated automatically (oldest customer order first).
                </p>
            </div>

            {loading ? (
                <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-500">Loading...</div>
            ) : orders.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-500">
                    <p className="font-medium text-gray-700">No open regional orders.</p>
                    <p className="text-xs mt-1">
                        Send a regional order from the <Link to="/admin/reorder" className="text-ghasc-navy hover:underline">Reorder page</Link>.
                    </p>
                </div>
            ) : (
                <div className="space-y-3">
                    {orders.map(o => (
                        <ReceivingCard
                            key={o.id}
                            order={o}
                            expanded={expandedId === o.id}
                            onToggle={() => setExpandedId(expandedId === o.id ? null : o.id)}
                            onReceived={load}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}

// ============================================================================
//  ReceivingCard — one regional order, expandable to the receive form
// ============================================================================
function ReceivingCard({ order, expanded, onToggle, onReceived }) {
    const sentDate = new Date(order.sentAt)
    const totalOrdered = order.items.reduce((s, i) => s + i.quantity, 0)
    const totalReceived = order.items.reduce((s, i) => s + (i.receivedQuantity || 0), 0)
    const allFullyReceived = order.items.every(i => (i.receivedQuantity || 0) >= i.quantity)
    const someReceived = totalReceived > 0

    return (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            {/* Header */}
            <button
                onClick={onToggle}
                className="w-full px-4 py-4 flex items-center justify-between hover:bg-gray-50 text-left"
            >
                <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-3 flex-wrap">
                        <span className="font-semibold text-ghasc-navy">Reorder #{order.id}</span>
                        <span className="text-xs text-gray-500">
                            Sent {sentDate.toLocaleDateString('en-US', {
                                month: 'short', day: 'numeric', year: 'numeric',
                            })}
                        </span>
                    </div>
                    <div className="text-sm text-gray-700 mt-1 break-words">
                        → {order.sentTo}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                        {totalReceived}/{totalOrdered} units received
                        {someReceived && !allFullyReceived && (
                            <span className="ml-2 text-amber-700">· partially received</span>
                        )}
                        {allFullyReceived && (
                            <span className="ml-2 text-ghasc-success">· all received (but order still open)</span>
                        )}
                    </div>
                </div>
                <span className="text-gray-400 ml-2 text-xl">{expanded ? '▾' : '▸'}</span>
            </button>

            {/* Expanded receive form */}
            {expanded && (
                <ReceiveForm order={order} onReceived={onReceived} />
            )}
        </div>
    )
}

// ============================================================================
//  ReceiveForm — the actual qty inputs + submit
// ============================================================================
function ReceiveForm({ order, onReceived }) {
    // Per-line draft state: { [regionalOrderItemId]: stringQty }
    // Defaults to (ordered - already received), or 0 if line is complete
    const [drafts, setDrafts] = useState(() => {
        const init = {}
        for (const item of order.items) {
            const remaining = item.quantity - (item.receivedQuantity || 0)
            init[item.id] = String(Math.max(0, remaining))
        }
        return init
    })
    const [busy, setBusy] = useState(false)

    function update(itemId, value) {
        setDrafts(d => ({ ...d, [itemId]: value }))
    }

    // Parse the drafts into items array, dropping zeros
    function buildPayload() {
        const items = []
        for (const item of order.items) {
            const n = parseInt(drafts[item.id], 10)
            if (!isNaN(n) && n > 0) {
                items.push({ regionalOrderItemId: item.id, receivedQuantity: n })
            }
        }
        return items
    }

    async function handleSubmit() {
        const items = buildPayload()
        if (items.length === 0) {
            toast.error('Enter at least one received quantity')
            return
        }

        // Check for over-receive — warn but allow
        const overrun = order.items.filter(item => {
            const n = parseInt(drafts[item.id], 10) || 0
            const totalAfter = (item.receivedQuantity || 0) + n
            return totalAfter > item.quantity
        })
        if (overrun.length > 0) {
            const names = overrun.map(i => i.description).join(', ')
            const ok = confirm(
                `Some lines have more units than originally ordered:\n\n${names}\n\nProceed anyway? Extra units will still go into inventory.`
            )
            if (!ok) return
        }

        const willCloseOrder = order.items.every(item => {
            const drafted = parseInt(drafts[item.id], 10) || 0
            return (item.receivedQuantity || 0) + drafted >= item.quantity
        })

        setBusy(true)
        try {
            const response = await regionalOrderApi.receive(order.id, items, willCloseOrder)
            if (response.success) {
                const s = response.summary || {}
                let msg = `Received ${s.linesReceived ?? items.length} line${s.linesReceived === 1 ? '' : 's'}`
                if (s.unitsFilled > 0) {
                    msg += ` · ${s.unitsFilled} backorder unit${s.unitsFilled === 1 ? '' : 's'} filled across ${s.ordersAffectedCount} order${s.ordersAffectedCount === 1 ? '' : 's'}`
                }
                if (s.regionalOrderClosed) msg += ' · order closed'
                toast.success(msg)
                onReceived()
            } else {
                toast.error(response.message || 'Receive failed')
            }
        } catch {
            toast.error('Network error')
        } finally {
            setBusy(false)
        }
    }

    return (
        <div className="border-t border-gray-100 p-4 bg-gray-50 space-y-3">
            {/* Per-item rows */}
            <div className="space-y-2">
                {order.items.map(item => {
                    const alreadyReceived = item.receivedQuantity || 0
                    const remaining = item.quantity - alreadyReceived
                    const drafted = parseInt(drafts[item.id], 10) || 0
                    const totalAfter = alreadyReceived + drafted
                    const isOverrun = totalAfter > item.quantity
                    const isComplete = alreadyReceived >= item.quantity
                    const variantLabel = [item.year, item.color].filter(Boolean).join(' / ')

                    return (
                        <div key={item.id} className="bg-white rounded-lg p-3">
                            <div className="flex items-start gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-gray-900 break-words">
                                        {item.description}
                                    </div>
                                    <div className="text-xs text-gray-500 font-mono mt-0.5">
                                        {item.itemNumber}
                                        {variantLabel && <span className="ml-2 font-sans">— {variantLabel}</span>}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">
                                        Ordered: <strong>{item.quantity}</strong>
                                        {alreadyReceived > 0 && (
                                            <span> · already received: <strong>{alreadyReceived}</strong></span>
                                        )}
                                        {remaining > 0 && !isComplete && (
                                            <span> · remaining: <strong>{remaining}</strong></span>
                                        )}
                                        {isComplete && (
                                            <span className="ml-2 text-ghasc-success">✓ Complete</span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex-shrink-0">
                                    <label className="block text-xs text-gray-500 mb-0.5 text-right">
                                        How many<br />Received
                                    </label>
                                    <input
                                        type="number"
                                        inputMode="numeric"
                                        min="0"
                                        value={drafts[item.id]}
                                        onChange={(e) => update(item.id, e.target.value)}
                                        className={`no-spinner w-20 px-2 py-1.5 border rounded-lg text-right text-base focus:outline-none focus:ring-2 focus:ring-ghasc-navy ${isOverrun ? 'border-amber-500 bg-amber-50' : 'border-gray-300'
                                            }`}
                                    />
                                </div>
                            </div>
                            {isOverrun && (
                                <div className="text-xs text-amber-700 mt-2 ml-1">
                                    ⚠ Over-receiving by {totalAfter - item.quantity} unit(s)
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>

            {/* Submit */}
            <div className="flex justify-end pt-2">
                <Button onClick={handleSubmit} loading={busy}>
                    Confirm Receiving
                </Button>
            </div>
        </div>
    )
}